import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";

const LangSwitch = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-[#191713]/20" role="group" aria-label="Language selector">
      {["FR", "EN", "PT"].map((l) => (
        <button
          key={l}
          data-testid={`lang-selector-${l.toLowerCase()}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1.5 text-[0.68rem] tracking-[0.15em] font-semibold transition-colors duration-200 ${
            lang === l ? "bg-[#191713] text-[#F7F3EC]" : "text-[#8A8378] hover:text-[#191713]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const { posts } = useContent();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname !== "/") navigate(`/#${id}`);
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { label: t.nav.services, id: "build" },
    { label: t.nav.industries, id: "industries" },
    { label: t.nav.pricing, to: "/pricing" },
    ...(posts?.length ? [{ label: t.nav.blog, to: "/blog" }] : []),
    { label: t.nav.contact, to: "/contact" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#F7F3EC]/92 backdrop-blur-md border-b border-[#191713]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Logo dark />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main">
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} data-testid={`nav-link-${l.to.slice(1)}`}
                className="text-sm text-[#191713]/75 hover:text-[#A6853B] transition-colors duration-200 tracking-wide">
                {l.label}
              </Link>
            ) : (
              <button key={l.label} onClick={() => goAnchor(l.id)} data-testid={`nav-anchor-${l.id}`}
                className="text-sm text-[#191713]/75 hover:text-[#A6853B] transition-colors duration-200 tracking-wide">
                {l.label}
              </button>
            )
          )}
        </nav>
        <div className="hidden lg:flex items-center gap-5">
          <LangSwitch />
          <Link to="/portal/login" data-testid="nav-portal-link"
            className="text-sm text-[#191713]/50 hover:text-[#A6853B] transition-colors duration-200">
            {t.nav.portal}
          </Link>
          <Link to="/contact" data-testid="nav-book-a-call" className="btn-dark !py-2.5 !px-5 text-xs">
            {t.nav.bookCall}
          </Link>
        </div>
        <div className="flex lg:hidden items-center gap-3">
          <LangSwitch compact />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu"
            className="text-[#191713] p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[#F7F3EC] border-t border-[#191713]/10 px-6 py-6 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-[#191713] text-base py-1">
                {l.label}
              </Link>
            ) : (
              <button key={l.label} onClick={() => goAnchor(l.id)} className="text-left text-[#191713] text-base py-1">
                {l.label}
              </button>
            )
          )}
          <Link to="/portal/login" onClick={() => setOpen(false)} className="text-[#9A9184] text-base py-1">
            {t.nav.portal}
          </Link>
          <Link to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-book-a-call" className="btn-dark mt-2">
            {t.nav.bookCall}
          </Link>
        </div>
      )}
    </header>
  );
};
